import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, signal, WritableSignal } from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import {
  PropertyCommonAreaType,
  PropertyCoOwnershipDto,
  PropertyCoOwnershipProcedureType,
} from '@workspace/data-types';
import { AbstractSignalFormComponent } from '@workspace/ng-aura';
import { EtxeEnumDisplayPipe } from '../../../pipes/etxe-enum-display.pipe';

export interface PropertyTrusteeModel {
  name: string;
  isOnline: boolean;
  hasOngoingProcedures: boolean;
  ongoingProcedures: PropertyCoOwnershipProcedureType[];
}

export interface PropertyCoOwnershipModel {
  isInCoOwnership: boolean | null;
  numberOfLots: number | null;
  lotNumber: string;
  trustee: PropertyTrusteeModel;
}

@Component({
  selector: 'etxe-property-co-ownership',
  imports: [
    CommonModule,
    FormField,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatListModule,
    EtxeEnumDisplayPipe,
  ],
  templateUrl: './co-ownership.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyCoOwnershipComponent extends AbstractSignalFormComponent<
  PropertyCoOwnershipDto,
  PropertyCoOwnershipModel
> {
  PropertyCommonAreaType = PropertyCommonAreaType;
  PropertyCoOwnershipProcedureType = PropertyCoOwnershipProcedureType;

  commonAreaTypes = Object.values(PropertyCommonAreaType);
  procedureTypes = Object.values(PropertyCoOwnershipProcedureType);

  override model: WritableSignal<PropertyCoOwnershipModel> = signal<PropertyCoOwnershipModel>({
    isInCoOwnership: null,
    numberOfLots: null,
    lotNumber: '',
    trustee: {
      name: '',
      isOnline: false,
      hasOngoingProcedures: false,
      ongoingProcedures: [],
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  override formTree = form(this.model, (_b) => {});

  protected override buildModelFromDto(dto: PropertyCoOwnershipDto): PropertyCoOwnershipModel {
    return {
      isInCoOwnership: dto?.isInCoOwnership ?? null,
      numberOfLots: dto?.numberOfLots ?? null,
      lotNumber: dto?.lotNumber ?? '',
      trustee: {
        name: dto?.trustee?.name ?? '',
        isOnline: dto?.trustee?.isOnline ?? false,
        hasOngoingProcedures: dto?.trustee?.hasOngoingProcedures ?? false,
        ongoingProcedures: dto?.trustee?.ongoingProcedures ?? [],
      },
    };
  }

  protected override buildDtoFromModel(model: PropertyCoOwnershipModel): PropertyCoOwnershipDto {
    return {
      isInCoOwnership: model.isInCoOwnership ?? undefined,
      numberOfLots:
        model.isInCoOwnership === true && model.numberOfLots != null ? Number(model.numberOfLots) : undefined,
      lotNumber: model.isInCoOwnership === true ? model.lotNumber || undefined : undefined,
      trustee:
        model.isInCoOwnership === true
          ? {
              name: model.trustee.name || undefined,
              isOnline: model.trustee.isOnline,
              hasOngoingProcedures: model.trustee.hasOngoingProcedures,
              ongoingProcedures: model.trustee.ongoingProcedures ?? [],
            }
          : undefined,
    } as PropertyCoOwnershipDto;
  }
}
